<template>
  <div class="field-wrapper-wuk" :class="{ 'invalid-field-wuk': validation && validation.$error, '--bg-fill': fillBg }">
    <div class="sword-checkbox-wuk" :class="{ minimal: variant === 'minimal' }">
      <base-input type="checkbox"
                  :name="name"
                  :id="uniqueId"
                  v-model="modelValue"
                  :opt-value="optValue"
                  :disabled="disabled"
                  :showError="showError"
                  :validation="validation" />
      <label
        :for="uniqueId"
        class="t3 -t_medium sword-checkbox-label"
        :class="{ icon: (icon || $slots.icon), }"
      >
        <div class="tick-box">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 13 13">
            <path fill="none"
                  fill-rule="evenodd"
                  stroke="#2972A8"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M10 15.4l3 3.6 8-8"
                  transform="translate(-9 -10)" />
          </svg>
        </div>

        <slot name="inner-content">
          <div class="sword-checkbox-label-display">
            <div v-if="(icon || $slots.icon) && variant === 'normal'" class="sword-checkbox-icon">
              <slot name="icon">
                  <sword-icon-wuk :name="icon" />
              </slot>
            </div>
            <span>
              <slot name="default"></slot>
            </span>
          </div>
        </slot>
      </label>
    </div>
    <input-error-wuk v-if="showError && Object.keys(validation).length"
                     :validator="validation"
                     :manual-error="manualError"></input-error-wuk>
  </div>
</template>

<script>
import inputHandler from '@ui-kit/mixins/inputHandler';
import BaseInput from '@ui-kit/components/inputs/BaseInput.vue';
import SwordIconWuk from '@ui-kit/components/icons/SwordIcon.vue';

export default {
  name: 'SwordCheckboxWuk',
  mixins: [inputHandler],
  components: { BaseInput, SwordIconWuk },
  model: {
    prop: 'checked',
    event: 'change',
  },
  props: {
    variant: {
      type: String,
      default: () => 'normal',
      validator(variant) {
        return ['normal', 'minimal'].includes(variant);
      },
    },
    checked: {
      type: [String, Number, Boolean, Array],
      required: true,
    },
    optValue: {
      type: [String, Number, Boolean],
      required: true,
    },
    name: {
      type: String,
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    showError: {
      type: Boolean,
      default: true,
    },
    icon: {
      type: String,
      default: null,
    }
  },
  computed: {
    modelValue: {
      get() {
        return this.checked;
      },
      set(check) {
        this.$emit('change', check);
      },
    },
  },
};
</script>

<style lang="scss" scoped>
$checked-tick-size: 55%;
$checkbox-outline: 2px solid #{$context-color-7};
$checkbox-outline-offset: 2px;

.invalid-field-wuk {
  input[type='checkbox'] {
    + label {
      .tick-box {
        border-color: $input-error-color !important;
      }
    }
  }
}

.sword-checkbox-wuk {
  input[type='checkbox'] {
    + label {
      .tick-box {
        border-radius: .25rem;

        svg {
          margin-top: 0.25rem;
          width: $checked-tick-size;
          height: $checked-tick-size;
        }
      }
    }

    &:checked {
      &:disabled {
        + label {
          .tick-box svg path {
            stroke: $context-color-7;
          }
        }
      }

      &:not(:disabled) {
        + label {
          .tick-box svg path {
            stroke: $context-color-1;
          }

          .tick-box {
            border-color: transparent;
          }
        }
      }
    }
  }

  &.minimal {
    input:focus {
      + label .tick-box {
        outline: $checkbox-outline;
        outline-offset: $checkbox-outline-offset;
        border-color: $context-color-1;
      }
    }
  }

  &:not(.minimal) {
    input[type='checkbox'] {
      + .sword-checkbox-label {
        background: $input-background-color-wuk;
        padding: 1.25rem 1rem;
        border-radius: 1rem;
        display: flex;
        gap: 1rem;
        justify-content: space-between;
        flex-direction: row-reverse;
        align-items: center;
        transition: background .15s ease-in-out;

        &.icon {
          padding-top: 0.75rem;
          padding-bottom: 0.75rem;
        }

        .tick-box {
          min-width: initial;
          width: 1.25rem;
          height: 1.25rem;
          align-self: auto;
          flex-shrink: 0;
        }

        .sword-checkbox-icon {
          padding: 0.625rem;
          border-radius: 0.75rem;
          background: $toggle-input-icon-background-color;
          display: flex;
          align-items: center;
          margin-right: 1rem;
        }

        .sword-checkbox-label-display {
          display: flex;
          align-items: center;
        }

        span {
          margin: 0;
          color: $toggle-input-text-color;
        }
      }

      &:checked {
        + .sword-checkbox-label {
          background: $toggle-input-checked-background-color;

          .sword-checkbox-icon {
            background: $toggle-input-icon-checked-background-color;
          }
        }
      }

      &:not(:checked) {
        + .sword-checkbox-label {
          .tick-box {
            background: $context-color-6;
          }
        }
      }

      &:hover:not(:disabled):not(:checked), &:active:not(:disabled):not(:checked)   {
        + label.sword-checkbox-label {
          background: $toggle-input-hover-background-color;
          ::v-deep {
            span {
              color: $toggle-input-hover-text-color !important;
            }
          }

          .tick-box {
            background: $white;
          }

          .sword-checkbox-icon {
            background: $toggle-input-icon-hover-background-color;
          }
        }
      }

      &:disabled {
        + .sword-checkbox-label {
          opacity: 1;
          background-color: $context-color-7;
          color: $context-color-4;

          .tick-box,
          .sword-checkbox-icon {
            background: $context-color-6;
          }

          .tick-box {
            svg path {
              stroke: $context-color-4;
            }
          }
        }
      }

      &:focus {
        + .sword-checkbox-label {
          outline: $checkbox-outline;
        }
      }
    }
  }
}
</style>
